
import { useParams, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { TeacherDocument } from '@/types/teacher.types';
import { toast } from 'sonner';

// Import related hooks
import { useTeacherFormState } from './teacher/useTeacherFormState';
import { useTeacherDepartments } from './teacher/useTeacherDepartments';
import { useTeacherUploads } from './teacher/useTeacherUploads';
import { useTeacherDetails } from './teacher/useTeacherDetails';
import { useTeacherSave } from './teacher/useTeacherSave';

/**
 * Main hook for the teacher form that combines all other teacher-related hooks
 */
export const useTeacherForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { formData, setFormData, handleChange } = useTeacherFormState();
  const { handleDepartmentChange, addDepartment } = useTeacherDepartments(formData, setFormData);
  const { handleImageUpload, setProfileImage, handleDocumentUpload, viewDocument, isUploading } = useTeacherUploads(formData, setFormData);
  const { saveTeacher: saveTeacherHook, isSaving } = useTeacherSave(formData);
  const [isRemovingDocument, setIsRemovingDocument] = useState(false);

  // Enhanced save function with navigation
  const saveTeacher = async () => {
    try {
      const savedTeacher = await saveTeacherHook();
      if (savedTeacher) {
        // Navigate to teachers list after successful save
        setTimeout(() => {
          navigate('/teachers');
        }, 1500); // Give user time to see success message
        return savedTeacher;
      }
    } catch (error) {
      // Error is already handled in useTeacherSave hook
      throw error;
    }
  };

  // Handle document removal directly through the form state
  const removeDocument = (index: number) => {
    if (isRemovingDocument) {
      toast.info("Document removal in progress, please wait");
      return;
    }
    
    console.log("Removing document at index:", index);
    setIsRemovingDocument(true);
    
    try {
      const updatedDocuments = [...(formData.documents || [])];
      
      if (index >= 0 && index < updatedDocuments.length) {
        const documentName = updatedDocuments[index].name || 'Document';
        updatedDocuments.splice(index, 1);
        
        setFormData(prev => ({
          ...prev,
          documents: updatedDocuments
        }));
        
        toast.success(`Document '${documentName}' removed successfully`);
      } else {
        toast.error("Could not remove document: invalid index");
      }
    } catch (error) {
      console.error("Error removing document:", error);
      toast.error("Failed to remove document");
    } finally {
      setIsRemovingDocument(false);
    }
  };

  // View document handler with error handling
  const handleViewDocument = (document: TeacherDocument) => {
    try {
      viewDocument(document);
    } catch (error) {
      console.error("Error viewing document:", error);
      toast.error("Failed to view document");
    }
  };

  // Fetch teacher data if editing
  useTeacherDetails(id, setFormData);

  return {
    formData,
    setFormData,
    handleChange,
    handleDepartmentChange,
    addDepartment,
    setProfileImage,
    handleImageUpload,
    handleDocumentUpload,
    removeDocument,
    viewDocument: handleViewDocument,
    saveTeacher,
    isUploading,
    isSaving
  };
};
